import { colors } from '@fs/zion-ui'

/**
 * Activity Color Theme Configuration
 *
 * @overview
 * The color themes are used throughout the application for:
 * - Activity cards and person cards
 * - Splash page backgrounds and elements
 * - Button styling and interactive elements
 * - Text color coordination
 * - Card component backgrounds
 *
 * @usage
 * ```javascript
 * import colors from '../pages/Activity/colors'
 *
 * // Get a color theme by name
 * const blueTheme = colors.blue
 *
 * // Use in component styling
 * const cardStyle = {
 *   backgroundColor: colors[colorTheme]?.card?.primary,
 *   color: colors[colorTheme]?.text
 * }
 *
 *
 * @structure
 * Each color theme contains the following properties:
 * - `splashBackground`: Background color for splash pages
 * - `background`: General background color
 * - `text`: Primary text color
 * - `button`: Button color theme identifier
 * - `card.primary`: Primary card background color
 *
 * @components
 * Used by components including:
 * - ActivityCard: Card backgrounds and styling
 * - PersonCard: Person-specific card theming
 * - SplashIcon: Splash page icon backgrounds
 * - SplashHeader: Header theming
 * - ActivityGroupSplashPhotoTimeline: Timeline color coordination
 * - DeleteButton: Button color theming
 * - Various helper functions for color selection
 *
 * @themes
 * Available color themes:
 * - `yellow`
 * - `red`
 * - `blue`
 * - `green`
 * - `purple`
 * @accessibility
 * All color combinations maintain appropriate contrast ratios for readability.
 * Text colors are chosen to provide sufficient contrast against their background colors.
 *
 * @dependencies
 * Imports color values from `@fs/zion-ui` design system to maintain consistency
 * with the broader FamilySearch design language.
 *
 * @see {@link ColorThemeType} - PropType definition in sharedPropTypes/activityProps.js
 * @see {@link getColor} - Helper function in helpers/activities_helper.js
 *
 * @since 1.0.0
 * @author Discovery Kids Team
 */

export default {
  yellow: {
    splashBackground: colors.yellow10,
    background: colors.yellow02,
    text: colors.yellow60,
    button: 'yellow',
    card: { primary: colors.yellow20 },
  },
  red: {
    splashBackground: colors.red10,
    background: colors.red02,
    text: colors.red60,
    button: 'red',
    card: { primary: colors.red20 },
  },
  blue: {
    splashBackground: colors.blue10,
    background: colors.blue02,
    text: colors.blue60,
    button: 'blue',
    card: { primary: colors.blue20 },
  },
  green: {
    splashBackground: colors.green10,
    background: colors.green02,
    text: colors.green60,
    button: 'green',
    card: { primary: colors.green20 },
  },
  purple: {
    splashBackground: colors.purple10,
    background: colors.purple02,
    text: colors.purple60,
    button: 'purple',
    card: { primary: colors.purple20 },
  },
}
